import React, { useState, useEffect, useContext } from "react";
import { getRequest, postRequest } from "../../../helpers/ApiHelpers";
import { css } from "emotion";
import {
  Button,
  Header,
  Grid,
  Form,
  TextArea,
  Dropdown,
  Input,
  Message
} from "semantic-ui-react";
import FileUpload from "../../FileUpload";
import { StateContext } from "../../State";
import { readFileAsBase64 } from "../../../helpers/ReadFileAsBase64";
import { logEvent } from "../../../helpers/analyticsLogger";

export function AddManual({ match, history }) {
  const { selectedState } = useContext(StateContext);

  const defaultManual = {
    stateId: selectedState,
    underwriterId: null,
    allUnderwriters: null,
    effectiveDate: null,
    note: null
  };

  const [state, setState] = useState({
    submitAttempted: false,
    submitting: false,
    states: [],
    underwriters: [],
    manual: defaultManual,
    file: null
  });

  function getErrorList() {
    let list = [];
    if (!state.manual.stateId) {
      list.push("You must select a state.");
    }
    if (!state.manual.underwriterId && state.manual.underwriterId !== 0) {
      list.push("You must select an underwriter.");
    }
    if (!state.manual.effectiveDate) {
      list.push("You must select an effective date.");
    }
    if (!state.file) {
      list.push("You must select a file.");
    }
    return list;
  }

  async function getUnderwriters() {
    const result = await getRequest("/underwriter/getUnderwriters");
    if (result.success) {
      setState((state) => ({
        ...state,
        underwriters: [
          { key: 0, value: 0, text: "All Underwriters" },
          ...result.data.map((u) => ({
            key: u.id,
            value: u.id,
            text: u.name
          }))
        ]
      }));
    }
  }

  async function getStates() {
    const result = await getRequest("/general/getStates");
    if (result.success) {
      setState((state) => ({
        ...state,
        states: result.data.map((s) => ({
          key: s.id,
          value: s.id,
          text: s.name
        }))
      }));
    }
  }

  useEffect(() => {
    getUnderwriters();
    getStates();
  }, []);

  useEffect(() => {
    setState((state) => ({ ...state, errorList: getErrorList() }));
  }, [state.manual, state.file]);

  async function saveChanges() {
    if (!state.submitting) {
      setState((state) => ({
        ...state,
        submitAttempted: true,
        submitting: !state.errorList.length > 0
      }));
      if (!state.errorList.length > 0) {
        const base64File = await readFileAsBase64(state.file);
        const fileExtension = state.file.name.split(".").pop();
        const manual = {
          ...state.manual,
          underwriterId: state.manual.allUnderwriters
            ? null
            : state.manual.underwriterId
        };
        const result = await postRequest(`/manual/addManual`, {
          ...manual,
          base64File,
          fileExtension
        });
        if (result.success) {
          logEvent(null, "Added", "Manual");
          setTimeout(function() {
            history.goBack();
          }, 1000);
        }
      }
    }
  }

  return (
    <div className={css({ width: 605, paddingBottom: 34 })}>
      <Header style={{ fontSize: 24, marginBottom: 41 }} as="h3">
        New Manual
      </Header>
      <Form>
        <Grid>
          <Grid.Row columns={1}>
            <Grid.Column>
              <Form.Field required>
                <label>State</label>
                <Dropdown
                  size="large"
                  fluid
                  selection
                  placeholder="Select State"
                  style={{ fontSize: 16 }}
                  options={state.states}
                  value={state.manual.stateId}
                  onChange={(_, { value }) =>
                    setState((state) => ({
                      ...state,
                      manual: { ...state.manual, stateId: value }
                    }))
                  }
                  error={
                    state.submitAttempted &&
                    (state.manual.stateId === "" ||
                      state.manual.stateId === null)
                  }
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1}>
            <Grid.Column>
              <Form.Field required>
                <label>Underwriter</label>
                <Dropdown
                  size="large"
                  fluid
                  selection
                  placeholder="Select Underwriter"
                  style={{ fontSize: 16 }}
                  options={state.underwriters}
                  value={state.manual.underwriterId}
                  onChange={(_, { value }) =>
                    setState((state) => ({
                      ...state,
                      manual: {
                        ...state.manual,
                        underwriterId: value,
                        allUnderwriters: value === 0
                      }
                    }))
                  }
                  error={
                    state.submitAttempted && state.manual.underwriterId === null
                  }
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1}>
            <Grid.Column>
              <Form.Field required>
                <label>Effective Date</label>
                <Input
                  type="date"
                  size="large"
                  className={css({
                    "& input": {
                      color: state.manual.effectiveDate
                        ? "#000 !important"
                        : "rgba(191, 191, 191, 0.87) !important"
                    }
                  })}
                  onChange={(_, { value }) =>
                    setState((state) => ({
                      ...state,
                      manual: { ...state.manual, effectiveDate: value }
                    }))
                  }
                  autoComplete="new-password"
                  error={
                    state.submitAttempted && state.manual.effectiveDate === null
                  }
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1}>
            <Grid.Column>
              <Form.Field>
                <label>Note</label>
                <TextArea
                  placeholder="Note"
                  style={{ minHeight: 96 }}
                  value={state.manual.note}
                  onChange={(_, { value }) =>
                    setState((state) => ({
                      ...state,
                      manual: {
                        ...state.manual,
                        note: value
                      }
                    }))
                  }
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1}>
            <Grid.Column>
              <Form.Field required>
                <FileUpload
                  setFileToApp={(file) =>
                    setState((state) => ({ ...state, file }))
                  }
                  fileList={state.file ? [state.file] : []}
                  deleteFileToApp={() =>
                    setState((state) => ({
                      ...state,
                      file: null
                    }))
                  }
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1}>
            <Grid.Column>
              <Message
                visible={state.submitAttempted && state.errorList.length > 0}
                error
                header="There was some errors with your submission"
                list={state.errorList}
                data-automation="message"
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1}>
            <Grid.Column
              floated="right"
              className={css({ textAlign: "right" })}
            >
              <Button
                size="large"
                className="cancelButton"
                onClick={() => history.goBack()}
                data-automation="cancel"
              >
                Cancel
              </Button>
              <Button
                size="large"
                className="saveButton"
                onClick={() => saveChanges()}
                data-automation="save"
              >
                {state.submitting ? "Submitting..." : "Save"}
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Form>
    </div>
  );
}
